'use client';

import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import {
  Heart,
  ListMusic,
  Loader2,
  Music,
  Pause,
  Play,
  Shuffle,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { AisLink as Link } from '@/components/aisearch/ais-link';
import { chitat, Volna } from '@/components/aisearch/audio-pleer';
import { KLYUCH_PLEERA } from '@/lib/pleer';
import {
  fonPleer,
  izbrannoe,
  useFon,
  useFonDlya,
  useIzbrannoe,
} from '@/components/aisearch/fon-pleer-store';
import { vklyuchitFonPleer } from '@/components/aisearch/fon-pleer-vklyuchenie';
import type { Trek } from '@/lib/fon-pleer';
import { peremeshat } from '@/lib/izbrannoe';
import { dlitelnostIzvestna, formatVremeni } from '@/lib/volna';

/** CSS-переменная: высота панели внизу экрана телефона, чтобы контент не прятался. */
const PEREMENNAYA_NIZA = '--fon-pleer-niz';

/**
 * CSS-переменная: высота панели вверху широкого экрана. По ней липкие
 * шапки витрины и кабинета прилипают не к краю окна, а под панель, и не
 * уходят под неё при прокрутке; по ней же сдвигаются якоря
 * (`scroll-padding-top` в globals.css) и липкое меню кабинета.
 */
const PEREMENNAYA_VERHA = '--fon-pleer-verh';

/** С этой ширины (`lg` в Tailwind) панель стоит вверху, а не внизу. */
const SHIROKIJ_EKRAN = '(min-width: 1024px)';

/** Где стоит панель: на витрине или в кабинете aisearch. */
export type MestoPaneli = 'vitrina' | 'kabinet';

/**
 * Оформление панели по месту. На телефоне одинаково — снизу экрана; на
 * широком экране — на самом верху страницы, выше плашки и шапки, на всю
 * ширину, и прилипает к верху окна при прокрутке (решение владельца
 * 26.10.2026). Слоем панель выше шапки своего раздела: её меню
 * избранного раскрывается вниз, поверх шапки.
 */
const OFORMLENIE: Record<MestoPaneli, { panel: string; vnutri: string }> = {
  vitrina: {
    // Шапка витрины — z-50.
    panel: 'z-40 lg:z-[51]',
    vnutri: 'max-w-[1280px] lg:px-6',
  },
  kabinet: {
    // На телефоне z-30, ниже шапки кабинета (z-40): иначе панель
    // закрывала бы низ выпадающего меню разделов. На широком экране
    // выше шапки — по той же причине, что на витрине.
    // ym-hide-content: как и вся рабочая область кабинета, панель не
    // попадает в запись Вебвизора (#212).
    panel: 'z-30 ym-hide-content lg:z-50',
    vnutri: 'lg:px-6',
  },
};

/**
 * Панель фонового плеера aisearch: кнопки, волна, избранное.
 *
 * Сам звук живёт не здесь, а в ядре (`FonPleerYadro`) в общем layout
 * локали: оно не перемонтируется ни при переходах внутри витрины, ни
 * при переходе сайт ↔ кабинет, и музыка не прерывается. Панель лишь
 * показывает его состояние, поэтому её смена при переходе незаметна.
 * Она же включает ядро (`vklyuchitFonPleer`): на чужих продуктах панели
 * нет, и ядро там не грузится вовсе.
 *
 * Пока ничего не запускали, панели нет; после перезагрузки страницы она
 * возвращается на паузе с той же позиции (автозапуск без нажатия
 * браузер не даст).
 *
 * На широком экране (от 1024 px) — полоса на самом верху страницы, выше
 * плашки и шапки; прилипает к верху окна, а липкая шапка встаёт под неё
 * (`--fon-pleer-verh`). На телефоне — снизу экрана; пока она видна,
 * страница получает нижний отступ по её высоте (`--fon-pleer-niz`), и
 * последние кнопки страницы не прячутся.
 */
export function FonPleerPanel({ mesto = 'vitrina' }: { mesto?: MestoPaneli }) {
  const t = useTranslations('ais_pleer');
  const s = useFon();
  const panelRef = useRef<HTMLDivElement>(null);
  const trek = s.trek;

  useEffect(() => {
    vklyuchitFonPleer();
  }, []);

  // Отступ под панель: на телефоне — снизу страницы, на широком экране —
  // сдвиг липких шапок. Пока панели нет, обе переменные сняты, и ничего
  // не сдвигается.
  const vidna = trek !== null;
  useEffect(() => {
    const kor = document.documentElement;
    const el = panelRef.current;
    const snyat = () => {
      kor.style.removeProperty(PEREMENNAYA_NIZA);
      kor.style.removeProperty(PEREMENNAYA_VERHA);
    };
    if (!vidna || !el) {
      snyat();
      return;
    }
    const shirokij = window.matchMedia(SHIROKIJ_EKRAN);
    const zamer = () => {
      const h = `${String(el.offsetHeight)}px`;
      kor.style.setProperty(shirokij.matches ? PEREMENNAYA_VERHA : PEREMENNAYA_NIZA, h);
      kor.style.removeProperty(shirokij.matches ? PEREMENNAYA_NIZA : PEREMENNAYA_VERHA);
    };
    zamer();
    const nabl = new ResizeObserver(zamer);
    nabl.observe(el);
    shirokij.addEventListener('change', zamer);
    return () => {
      nabl.disconnect();
      shirokij.removeEventListener('change', zamer);
      snyat();
    };
  }, [vidna]);

  if (!trek) return null;

  const oform = OFORMLENIE[mesto];
  const p = fonPleer();
  const knopka =
    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text)] focus-visible:outline-2 focus-visible:outline-[var(--color-brand-500)] disabled:opacity-40';

  // На широком экране фон сплошной: полупрозрачная панель с размытием
  // пропускала у нижнего края текст страницы, и под плеером виднелась
  // «щель» (владелец, 29.10.2026). Внизу экрана на телефоне — как была.
  return (
    <div
      ref={panelRef}
      role="region"
      aria-label={t('fon_pleer')}
      data-fon-pleer
      data-mesto={mesto}
      className={`fixed inset-x-0 bottom-0 border-t border-[var(--color-border)] bg-[var(--color-bg)]/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(0,0,0,0.12)] backdrop-blur-lg lg:sticky lg:bg-[var(--color-bg)] lg:backdrop-blur-none lg:bottom-auto lg:top-0 lg:border-b lg:border-t-0 lg:pb-0 lg:pt-[env(safe-area-inset-top,0px)] lg:shadow-none ${oform.panel}`}
    >
      <div
        className={`mx-auto flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 lg:flex-nowrap lg:gap-x-3 lg:py-1.5 ${oform.vnutri}`}
      >
        <Oblozhka trek={trek} />

        <div className="order-2 min-w-0 flex-1 lg:w-64 lg:flex-none">
          <Link
            href={trek.href}
            className="block truncate text-sm font-medium leading-tight hover:underline"
          >
            {trek.title}
          </Link>
          <span className="block truncate text-xs tabular-nums text-[var(--color-text-muted)]">
            {formatVremeni(s.tek)} / {dlitelnostIzvestna(s.dlit) ? formatVremeni(s.dlit) : '–:––'}
            {s.podbiraem && <> · {t('podbiraem')}</>}
            {s.vDrugojVkladke && (
              <span data-v-drugoj-vkladke>
                {' · '}
                {s.igraet || s.zhdyot ? t('igraet_v_drugoj_vkladke') : t('pauza_v_drugoj_vkladke')}
              </span>
            )}
          </span>
        </div>

        <div className="order-3 flex items-center gap-0.5 lg:order-5">
          <Gromkost className={knopka} />
          <KnopkaIzbrannogo trek={trek} className={knopka} />
          <MenyuIzbrannogo className={knopka} vverh />
          <button
            type="button"
            onClick={() => {
              p.zakryt();
            }}
            aria-label={t('zakryt')}
            className={knopka}
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        </div>

        {/* На телефоне управление и волна — второй строкой. */}
        <div aria-hidden className="order-4 h-0 basis-full lg:hidden" />

        <div className="order-5 flex items-center gap-0.5 lg:order-3">
          <button
            type="button"
            onClick={() => {
              p.nazad();
            }}
            aria-label={t('nazad')}
            className={knopka}
          >
            <SkipBack aria-hidden className="h-4 w-4" fill="currentColor" />
          </button>
          <button
            type="button"
            onClick={() => {
              p.pereklyuchit();
            }}
            aria-label={s.igraet ? t('pauza') : t('igrat')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-brand-solid,var(--color-brand-500))] text-[var(--color-on-brand)] transition-colors hover:bg-[var(--color-brand-400)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-500)]"
          >
            {s.zhdyot ? (
              <Loader2 aria-hidden className="h-4 w-4 motion-safe:animate-spin" />
            ) : s.igraet ? (
              <Pause aria-hidden className="h-4 w-4" fill="currentColor" />
            ) : (
              <Play aria-hidden className="ml-0.5 h-4 w-4" fill="currentColor" />
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              void p.vpered();
            }}
            disabled={s.podbiraem}
            aria-label={t('vpered')}
            className={knopka}
          >
            <SkipForward aria-hidden className="h-4 w-4" fill="currentColor" />
          </button>
        </div>

        <div className="order-6 min-w-0 flex-1 lg:order-4">
          <Volna
            // Своя волна у каждого трека: столбики не переносятся.
            key={trek.id}
            id={trek.id}
            peaks={trek.peaks}
            tek={s.tek}
            dlit={s.dlit}
            vysota="h-8"
            onPeremotat={(v, zapuskat) => {
              p.peremotat(v);
              if (zapuskat && !s.igraet) p.igrat();
            }}
          />
        </div>
      </div>
    </div>
  );
}

function Oblozhka({ trek }: { trek: Trek }) {
  return (
    <span className="order-1 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]">
      {trek.oblozhka ? (
        <img src={trek.oblozhka} alt="" className="h-full w-full object-cover" />
      ) : (
        <Music aria-hidden className="h-5 w-5" />
      )}
    </span>
  );
}

/** Сердечко: в избранное и обратно. Работает и у гостя: хранится в браузере. */
export function KnopkaIzbrannogo({ trek, className }: { trek: Trek; className?: string }) {
  const t = useTranslations('ais_pleer');
  const spisok = useIzbrannoe();
  const est = spisok.some((x) => x.id === trek.id);
  return (
    <button
      type="button"
      data-izbrannoe={est ? 'da' : 'net'}
      onClick={() => {
        izbrannoe().pereklyuchit(trek);
      }}
      aria-pressed={est}
      aria-label={est ? t('iz_izbrannogo') : t('v_izbrannoe')}
      title={est ? t('iz_izbrannogo') : t('v_izbrannoe')}
      className={className ?? KNOPKA_KARTOCHKI}
    >
      <Heart
        aria-hidden
        className={`h-4 w-4 ${est ? 'text-[var(--color-brand-500)]' : ''}`}
        fill={est ? 'currentColor' : 'none'}
      />
    </button>
  );
}

const KNOPKA_KARTOCHKI =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--color-border)] text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-brand-500)]/60 hover:text-[var(--color-text)] focus-visible:outline-2 focus-visible:outline-[var(--color-brand-500)]';

/**
 * «Слушать в плеере» и сердечко у трека: на карточке, на странице работы,
 * в строке папки. Позиция переносится: если трек уже играл во встроенном
 * плеере рядом (общий предок с `data-trek`), фоновый продолжит с того же места.
 */
export function KnopkiTreka({ trek, podpis = false }: { trek: Trek; podpis?: boolean }) {
  const t = useTranslations('ais_pleer');
  const vFone = useFonDlya(trek.id) !== null;

  const vPleer = (e: MouseEvent<HTMLButtonElement>) => {
    const ryadom = e.currentTarget.closest('[data-trek]')?.querySelector('audio');
    const start = ryadom && !ryadom.ended && ryadom.currentTime > 0 ? ryadom.currentTime : 0;
    fonPleer().slushat(trek, start);
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        data-v-pleer
        onClick={vPleer}
        aria-pressed={vFone}
        aria-label={t('v_pleer')}
        title={t('v_pleer')}
        className={
          podpis
            ? 'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-[var(--color-border)] px-3 text-xs font-medium text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-brand-500)]/60 hover:text-[var(--color-text)] focus-visible:outline-2 focus-visible:outline-[var(--color-brand-500)] aria-pressed:border-[var(--color-brand-500)] aria-pressed:text-[var(--color-brand-500)]'
            : `${KNOPKA_KARTOCHKI} aria-pressed:border-[var(--color-brand-500)] aria-pressed:text-[var(--color-brand-500)]`
        }
      >
        <ListMusic aria-hidden className="h-4 w-4" />
        {podpis && <span>{vFone ? t('v_pleere') : t('v_pleer')}</span>}
      </button>
      <KnopkaIzbrannogo trek={trek} />
    </span>
  );
}

/** «Слушать всю папку»: все звуки папки по порядку в очередь фонового плеера. */
export function KnopkaPapki({ treki }: { treki: Trek[] }) {
  const t = useTranslations('ais_pleer');
  if (treki.length === 0) return null;
  return (
    <button
      type="button"
      data-vsya-papka
      onClick={() => {
        fonPleer().slushatSpisok(treki, 0);
      }}
      className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-brand-solid,var(--color-brand-500))] px-4 py-2 text-sm font-semibold text-[var(--color-on-brand)] transition-colors hover:bg-[var(--color-brand-400)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-500)]"
    >
      <Play aria-hidden className="h-4 w-4" fill="currentColor" />
      {t('vsya_papka', { count: treki.length })}
    </button>
  );
}

/**
 * Избранное: выпадающий список с «Играть по порядку» и «Вперемешку».
 * Отдельной страницы нет: избранное живёт в браузере, и публичный адрес
 * ему ни к чему.
 */
export function MenyuIzbrannogo({
  className,
  vverh = false,
  sPodpisyu = false,
}: {
  className?: string;
  /** Открывать вверх: панель на телефоне стоит внизу экрана. */
  vverh?: boolean;
  /** Кнопка с подписью и числом (на витрине), а не значок (в панели). */
  sPodpisyu?: boolean;
}) {
  const t = useTranslations('ais_pleer');
  const spisok = useIzbrannoe();
  const [otkryto, setOtkryto] = useState(false);
  const koren = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!otkryto) return;
    const naKlik = (e: Event) => {
      if (!koren.current?.contains(e.target as Node)) setOtkryto(false);
    };
    const naKlavishu = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOtkryto(false);
    };
    document.addEventListener('pointerdown', naKlik);
    document.addEventListener('keydown', naKlavishu);
    return () => {
      document.removeEventListener('pointerdown', naKlik);
      document.removeEventListener('keydown', naKlavishu);
    };
  }, [otkryto]);

  if (sPodpisyu && spisok.length === 0) return null;

  const igrat = (treki: readonly Trek[], ot = 0) => {
    fonPleer().slushatSpisok(treki, ot);
    setOtkryto(false);
  };

  return (
    <div ref={koren} className="relative">
      <button
        type="button"
        data-menyu-izbrannogo
        onClick={() => {
          setOtkryto(!otkryto);
        }}
        aria-expanded={otkryto}
        aria-haspopup="dialog"
        aria-label={t('izbrannoe')}
        title={t('izbrannoe')}
        className={
          sPodpisyu
            ? 'inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-3 py-1.5 text-sm font-medium text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)]'
            : className
        }
      >
        {sPodpisyu ? (
          <Heart
            aria-hidden
            className="h-4 w-4 text-[var(--color-brand-500)]"
            fill="currentColor"
          />
        ) : (
          <ListMusic aria-hidden className="h-4 w-4" />
        )}
        {sPodpisyu && (
          <span>
            {t('izbrannoe')} <span className="opacity-70">{spisok.length}</span>
          </span>
        )}
      </button>
      {otkryto && (
        <div
          role="dialog"
          aria-label={t('izbrannoe')}
          className={`absolute z-50 w-[min(20rem,calc(100vw-1.5rem))] rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 shadow-xl ${
            vverh
              ? 'bottom-full right-0 mb-2 lg:bottom-auto lg:top-full lg:mb-0 lg:mt-2'
              : 'left-0 top-full mt-2'
          }`}
        >
          <p className="mb-2 text-sm font-semibold">{t('izbrannoe')}</p>
          {spisok.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('izbrannoe_pusto')}</p>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  data-igrat-izbrannoe
                  onClick={() => {
                    igrat(spisok);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--color-brand-solid,var(--color-brand-500))] px-3 py-1.5 text-xs font-semibold text-[var(--color-on-brand)] hover:bg-[var(--color-brand-400)]"
                >
                  <Play aria-hidden className="h-3.5 w-3.5" fill="currentColor" />
                  {t('po_poryadku')}
                </button>
                <button
                  type="button"
                  data-vpermeshku
                  onClick={() => {
                    igrat(peremeshat(spisok));
                  }}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--color-border)] px-3 py-1.5 text-xs font-semibold hover:bg-[var(--color-bg-elevated)]"
                >
                  <Shuffle aria-hidden className="h-3.5 w-3.5" />
                  {t('vpermeshku')}
                </button>
              </div>
              <ul className="max-h-[50vh] divide-y divide-[var(--color-border)] overflow-y-auto">
                {spisok.map((x, i) => (
                  <li key={x.id} className="flex items-center gap-2 py-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        igrat(spisok, i);
                      }}
                      className="min-w-0 flex-1 truncate text-left text-sm hover:underline"
                    >
                      {x.title}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        izbrannoe().ubrat(x.id);
                      }}
                      aria-label={t('ubrat', { name: x.title })}
                      className="shrink-0 rounded p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    >
                      <X aria-hidden className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Громкость фонового плеера (просьба владельца 29.10.2026).
 *
 * Только там, где есть мышь: на телефоне громкость у кнопок самого
 * телефона, а iOS её из страницы не меняет. Настройка общая с плеером
 * карточки (`KLYUCH_PLEERA`); если звучит другая вкладка, она подхватит
 * новое значение по событию `storage` (fon-pleer-yadro.tsx).
 */
function Gromkost({ className }: { className: string }) {
  const t = useTranslations('ais_pleer');
  const [n, setN] = useState(() => ({ gromkost: 1, bezZvuka: false }));

  useEffect(() => {
    setN(chitat());
  }, []);

  const primenit = (gromkost: number, bezZvuka: boolean) => {
    setN({ gromkost, bezZvuka });
    fonPleer().zvuk(gromkost, bezZvuka);
    try {
      localStorage.setItem(KLYUCH_PLEERA, JSON.stringify({ ...chitat(), gromkost, bezZvuka }));
    } catch {
      // Хранилище закрыто — громкость действует до перезагрузки.
    }
  };

  const tiho = n.bezZvuka || n.gromkost === 0;
  return (
    <span className="hidden items-center [@media(pointer:fine)]:lg:flex">
      <button
        type="button"
        onClick={() => {
          // Включая звук на нуле, возвращаем слышимую громкость.
          if (tiho) primenit(n.gromkost === 0 ? 0.5 : n.gromkost, false);
          else primenit(n.gromkost, true);
        }}
        aria-label={tiho ? t('zvuk_vkl') : t('zvuk_vykl')}
        aria-pressed={tiho}
        className={className}
      >
        {tiho ? (
          <VolumeX aria-hidden className="h-4 w-4" />
        ) : (
          <Volume2 aria-hidden className="h-4 w-4" />
        )}
      </button>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={n.bezZvuka ? 0 : n.gromkost}
        onChange={(e) => {
          const g = Number(e.target.value);
          primenit(g, g === 0);
        }}
        aria-label={t('gromkost')}
        className="mr-1 h-1 w-20 cursor-pointer accent-[var(--color-brand-500)]"
      />
    </span>
  );
}
